import React from "react";
import { TFunction } from "i18next";

import { UploadRow, RowStatus } from "@context/subscriberSyncUpload/reducer";

// Row-status → Tag mapping, shared by the Review table and the Row Detail modal.
export const STATUS_TAG: Record<
  RowStatus,
  { status: "success" | "error" | "warning"; key: string }
> = {
  in_sync: {
    status: "success",
    key: "subscriber_sync_upload.row_status.in_sync",
  },
  has_differences: {
    status: "warning",
    key: "subscriber_sync_upload.row_status.has_differences",
  },
  not_found: {
    status: "error",
    key: "subscriber_sync_upload.row_status.not_found",
  },
  invalid: { status: "error", key: "subscriber_sync_upload.row_status.invalid" },
};

// Renders a row's field differences (has_differences) or its format errors
// (invalid); null for in_sync / not_found. Shared by the Review table and the
// Row Detail modal so both render diffs identically. Takes `t` (not a hook — this
// is a plain helper) to translate format-error codes, interpolating the offending
// value from rowData[field].
export const renderRowDiffs = (row: UploadRow, t: TFunction) => {
  const rowData = (row.rowData ?? {}) as Record<string, unknown>;
  if (row.rowStatus === "has_differences") {
    return (
      <dl className="text-xs space-y-1">
        {row.diffs.map(diff => (
          <div key={diff.field} className="flex gap-2">
            <dt className="font-medium text-gray-700">{diff.field}:</dt>
            <dd className="flex gap-1 items-center">
              <span className="text-gray-500 line-through">
                {diff.current ?? "—"}
              </span>
              <span aria-hidden className="text-gray-400">
                →
              </span>
              <span className="text-gray-900">{diff.proposed}</span>
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  if (row.rowStatus === "invalid") {
    return (
      <ul className="text-xs space-y-1 text-red-700">
        {row.formatErrors.map((err, i) => (
          <li key={`${err.code}-${i}`}>
            {t(`subscriber_sync_upload.modal.error.${err.code}`, {
              value: err.field ? (rowData[err.field] as string) ?? "" : "",
            })}
          </li>
        ))}
      </ul>
    );
  }
  return null;
};
